import { useNavigate } from 'react-router';
import { useGetUsers, type User } from '../api/useGetUsers';
import { DataTable, type Column } from './DataTable';
import { Pagination } from './Pagination';
import { SearchInput } from './SearchInput';
import { AccountStatusBadge } from './AccountStatusBadge';
import { AccountStatusAction } from './AccountStatusAction';
import { useUrlSearch } from '../hooks/useUrlSearch';
import { useFittedPageSize } from '../hooks/useFittedPageSize';

const columns: Column<User>[] = [
  { key: 'uid', header: 'User ID', className: 'font-mono text-xs text-gray-400', cell: (u) => `${u.uid.slice(0, 8)}…`, copyValue: (u) => u.uid },
  { key: 'name', header: 'Name', className: 'text-gray-900', cell: (u) => `${u.first_name} ${u.last_name}`, copyValue: (u) => `${u.first_name} ${u.last_name}` },
  { key: 'email', header: 'Email', cell: (u) => u.email, copyValue: (u) => u.email },
  { key: 'phone', header: 'Phone', cell: (u) => u.phone, copyValue: (u) => u.phone },
  {
    key: 'joined',
    header: 'Joined',
    className: 'text-gray-500',
    cell: (u) => new Date(u.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
  },
  { key: 'status', header: 'Status', cell: (u) => <AccountStatusBadge status={u.status} remarks={u.remarks} /> },
  {
    key: 'actions',
    header: '',
    cell: (u) => (
      <AccountStatusAction url={`/v1/ops/users/${u.uid}/status`} cacheKey="users" status={u.status} name={`${u.first_name} ${u.last_name}`} />
    ),
  },
];

export const GuestsTable = () => {
  const navigate = useNavigate();
  const { query, page, search, setSearch, goToPage } = useUrlSearch();
  const { tableRef, pageSize } = useFittedPageSize();
  const { data, isLoading, isError } = useGetUsers({ page, limit: pageSize, q: query || undefined });

  const users = data?.payload?.users ?? [];
  const total = data?.payload?.total ?? 0;
  const showPagination = !isLoading && !isError && users.length > 0;

  return (
    <div className="flex flex-col gap-4">
      <SearchInput value={search} onChange={setSearch} placeholder="Search by name or email" label="Search guests" />
      <div ref={tableRef}>
        <DataTable
          columns={columns}
          rows={users}
          rowKey={(u) => u.uid}
          isLoading={isLoading}
          isError={isError}
          errorLabel="Failed to load guests."
          emptyLabel="No guests found."
        />
      </div>
      {showPagination && (
        <Pagination page={page} pageSize={pageSize} total={total} onPageChange={goToPage} />
      )}
    </div>
  );
};
